// @flow

import * as React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import {
  colors,
  fontSize,
  fontWeight,
  type ReskinThemeType,
} from '../constants';
import { Card, CardHeaderContainer, CardBody } from './card';
import PrimaryPalette from '../../birch/colors';
import { buildVersionSelector } from '../constants';

let globalTabCardCount = 0;

const tabbedCardUUID = (): string => {
  return `TabbedCard-${globalTabCardCount++}`;
};

// This is needed for test cases;
const resetTabbedCardUUID = () => {
  globalTabCardCount = 0;
};

export type TabbedCardProps = {
  minimal: boolean,
  compact: boolean,
  theme: 'ghr' | 'ghr-dark' | 'gho',
  onTabSelect?: ({ index: number }) => void,
  selectedIndex?: number,
  children?: React.Node,
  rightComponent?: React.Node,
};

type TabbedCardState = {
  selectedIndex: number,
  numTabs: number,
  focus?: boolean,
};

const azaleaVersion = {
  backgroundColor: colors.lightestGrey,
  borderColor: colors.lightGrey,
};

const birchVersion = {
  backgroundColor: PrimaryPalette.white,
  borderColor: PrimaryPalette.darkGrey,
};

const versionSelector = buildVersionSelector(azaleaVersion, birchVersion);

class TabbedCard extends React.Component<TabbedCardProps, TabbedCardState> {
  uuid: string;

  static defaultProps = {
    compact: false,
    minimal: false,
    theme: 'ghr',
  };

  get selectedIndex() {
    if (this.props.selectedIndex) {
      return this.props.selectedIndex;
    } else {
      return this.state.selectedIndex;
    }
  }

  constructor(props: TabbedCardProps) {
    super(props);

    this.uuid = tabbedCardUUID();
    this.state = {
      selectedIndex: 0,
      numTabs: React.Children.count(props.children),
    };
  }

  handleSelect =
    (index: number, focus: boolean = false) =>
    () => {
      if (this.props.onTabSelect) {
        this.props.onTabSelect({ index });
      } else {
        this.selectTab(index, focus);
      }
    };

  selectTab = (index: number, focus: boolean = false) => {
    this.setState({
      selectedIndex: index,
      focus: focus,
    });
  };

  getPrevTab(index: number) {
    let newIndex = index - 1;

    if (newIndex < 0) {
      newIndex = this.state.numTabs - 1;
    }

    return newIndex;
  }

  getNextTab(index: number) {
    let newIndex = index + 1;

    if (newIndex >= this.state.numTabs) {
      newIndex = 0;
    }

    return newIndex;
  }

  tabId = (index: number) => {
    return `${this.uuid}-Header-${index + 1}`;
  };

  panelId(index: number) {
    return `${this.uuid}-Panel-${index + 1}`;
  }

  handleKeyDown = (e: KeyboardEvent) => {
    let index = 0;
    let isTabSwitch = false;

    // Select next tab to the left
    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      index = this.getPrevTab(this.selectedIndex);
      isTabSwitch = true;
    }
    // Select next tab to the right
    else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      index = this.getNextTab(this.selectedIndex);
      isTabSwitch = true;
    }

    // This prevents scrollbars from moving around
    if (isTabSwitch) {
      e.preventDefault();
      this.selectTab(index, true);
    }
  };

  renderTabHeaders() {
    const { minimal, theme } = this.props;
    let index = 0;

    const tabHeaders = React.Children.map(this.props.children, (child) => {
      if (child === null) {
        return null;
      }

      const curIndex = index;
      index++;

      const selected = this.selectedIndex === curIndex;

      return (
        <TabHeader
          onSelectFn={this.handleSelect(curIndex)}
          selected={selected}
          focus={this.state.focus}
          id={this.tabId(curIndex)}
          panelId={this.panelId(curIndex)}
          minimal={minimal}
          className={child.props.headerClassName}
          theme={theme}
        >
          {child.props.title}
        </TabHeader>
      );
    });

    return (
      <TabList onKeyDown={this.handleKeyDown} role="tablist">
        {tabHeaders}
      </TabList>
    );
  }

  renderTabs() {
    let index = 0;

    return React.Children.map(this.props.children, (child) => {
      if (child === null) {
        return null;
      }

      const curIndex = index;
      index++;
      const selected = this.selectedIndex === curIndex;

      return React.cloneElement(child, {
        selected: selected,
        id: this.panelId(curIndex),
        tabId: this.tabId(curIndex),
        compact: this.props.compact,
      });
    });
  }

  render() {
    // Make sure "...rest" does not include children and rightComponent so they are not passed to <Card/>
    // eslint-disable-next-line no-unused-vars
    const { children, minimal, theme, rightComponent, ...rest } = this.props;

    return (
      <Card useLegacyPadding {...rest}>
        <TabHeaderContainer minimal={minimal} repositoryTheme={theme}>
          {this.renderTabHeaders()}
          {rightComponent}
        </TabHeaderContainer>
        {this.renderTabs()}
      </Card>
    );
  }
}

export type TabProps = {
  title: string,
  selected: boolean,
  minimal: boolean,
  children: React.Node,
};

class Tab extends React.Component<TabProps> {
  static propTypes = Object.assign(
    {},
    {
      title: PropTypes.string.isRequired,
      selected: PropTypes.bool.isRequired,
      minimal: PropTypes.bool,
    },
    CardBody.propTypes
  );

  static defaultProps = {
    minimal: false,
    selected: false,
  };
  // $FlowFixMe
  renderBody(attrs) {
    // eslint-disable-next-line no-unused-vars
    const { title, ...restProps } = attrs;

    return (
      <TabBody verticalCardGutter="none" {...restProps}>
        {this.props.children}
      </TabBody>
    );
  }

  render() {
    // $FlowFixMe eslint-disable-next-line no-unused-vars
    const { tabId, id, selected, children, ...rest } = this.props;

    return (
      <div aria-labelledby={tabId} id={id} role="tabpanel">
        {selected && this.renderBody(rest)}
      </div>
    );
  }
}

type TabHeaderProps = {
  onSelectFn: () => void,
  id: string,
  panelId: string,
  selected: boolean,
  minimal: boolean,
  theme: 'ghr' | 'ghr-dark' | 'gho',
  focus?: boolean,
  children: React.Node,
};

class TabHeader extends React.Component<TabHeaderProps> {
  static defaultProps = {
    selected: false,
    focus: false,
  };

  headerRef = React.createRef<HTMLLIElement>();

  onClick = (e) => {
    e.preventDefault();
    this.props.onSelectFn();
  };

  componentDidMount() {
    this.checkFocus();
  }

  componentDidUpdate() {
    this.checkFocus();
  }

  checkFocus() {
    if (this.props.selected && this.props.focus) {
      this.headerRef.current && this.headerRef.current.focus();
    }
  }

  render() {
    return (
      <TabHeaderListItem
        id={this.props.id}
        onClick={this.onClick}
        ref={this.headerRef}
        role="tab"
        tabIndex={this.props.selected ? '0' : null}
        selected={this.props.selected}
        aria-selected={this.props.selected}
        aria-expanded={this.props.selected}
        aria-controls={this.props.panelId}
        repositoryTheme={this.props.theme}
        minimal={this.props.minimal}
      >
        <TabHeaderWrapper>
          <TabHeaderContent>{this.props.children}</TabHeaderContent>
          <TabHeaderHighlight
            selected={this.props.selected}
            repositoryTheme={this.props.theme}
          />
        </TabHeaderWrapper>
      </TabHeaderListItem>
    );
  }
}

type BorderColorArguments = {
  repositoryTheme: string,
  theme?: { name: ReskinThemeType },
};

const borderColor = ({
  repositoryTheme,
  theme,
}: BorderColorArguments): string => {
  switch (repositoryTheme) {
    case 'ghr':
      return versionSelector(theme?.name).borderColor;
    case 'gho':
      return colors.lightGrey;
    case 'ghr-dark':
      return colors.darkerGrey;
    default:
      throw new Error(ThemeErrorMessage(repositoryTheme));
  }
};

type BackgroundColorArguments = {
  repositoryTheme: string,
  theme?: { name: ReskinThemeType },
};

const backgroundColor = ({
  repositoryTheme,
  theme,
}: BackgroundColorArguments) => {
  switch (repositoryTheme) {
    case 'ghr':
      return versionSelector(theme?.name).backgroundColor;
    case 'gho':
      return colors.lightestGrey;
    case 'ghr-dark':
      return colors.darkerGrey;
    default:
      throw new Error(ThemeErrorMessage(repositoryTheme));
  }
};

const highlightedColor = ({
  selected,
  repositoryTheme,
}: {
  selected: boolean,
  repositoryTheme: string,
}): string => {
  if (!selected) {
    return 'transparent';
  }

  switch (repositoryTheme) {
    case 'ghr':
    case 'ghr-dark':
      return colors.mediumGreenhouseGreen;
    case 'gho':
      return colors.ghoBlue;
    default:
      throw new Error(ThemeErrorMessage(repositoryTheme));
  }
};

const textWeight = ({ selected }: { selected: boolean }): string => {
  return selected ? fontWeight('heavy') : fontWeight('normal');
};

const textColor = ({
  selected,
  repositoryTheme,
}: {
  selected: boolean,
  repositoryTheme: string,
}): string => {
  switch (repositoryTheme) {
    case 'ghr':
    case 'gho':
      return colors.darkestGrey;
    case 'ghr-dark':
      return selected ? colors.white : colors.lightGrey;
    default:
      throw new Error(ThemeErrorMessage(repositoryTheme));
  }
};

const minimalStyling = css`
  padding-left: 0;
  padding-right: 0;

  border: 0;
  border-radius: 0;
  background-color: transparent;
`;

const TabHeaderContainer = styled(CardHeaderContainer)`
  padding: 0;
  background-color: ${backgroundColor};
  border: 1px solid ${borderColor};
  display: flex;
  align-items: center;

  ${(props) =>
    props.minimal &&
    css`
      ${minimalStyling}

      & [role="tablist"] {
        border-bottom: 1px solid ${colors.lightGrey};
      }
    `}
`;

/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.92.0. To view the error, delete this comment and run Flow. */
const TabList = styled.ul`
  padding: 0;
  margin: 0;
  width: 100%;
  height: 55px;
  display: flex;
  align-items: center;
`;

const TabHeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TabHeaderHighlight = styled.div`
  background: ${(props) => highlightedColor(props)};
  border: 2px solid ${(props) => highlightedColor(props)};
  border-radius: 1px;
  width: 32px;
`;

const TabHeaderListItem = styled.li`
  margin: 0 25px;
  display: flex;
  align-items: strech;
  list-style: none;
  justify-content: center;
  cursor: pointer;
  color: ${(props) => textColor(props)};
  height: 100%;
  box-sizing: border-box;
  font-size: 14px;
  ${(props) => textWeight(props)}

  ${(props) =>
    props.minimal &&
    css`
      &:first-child {
        margin-left: 0;
      }

      padding: 0;
      font-size: ${fontSize.large};
      ${fontWeight('heavy')}
    `}
`;

/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.104.0. To view the error, delete this comment and run Flow. */
const TabBody = styled(CardBody)`
  // if minimal mode is enabled, remove all background and border styling
  ${(props) =>
    props.minimal &&
    css`
      ${minimalStyling};
    `}
`;

const TabHeaderContent = styled.div`
  padding: 0 1px;
  flex-grow: 1;
  align-items: center;
  display: flex;
`;

const ThemeErrorMessage = (theme) => {
  return `Invalid theme given "${theme}". Only (ghr|gho|ghr-dark) are valid themes`;
};

export { TabbedCard, Tab, TabList, resetTabbedCardUUID };
